import { createI18n } from 'vue-i18n'
import { loadAllYamlTranslations } from './yamlLoader'
import { preloadCommonLanguagePacks } from './packageLoader'
import { resolveInitialLanguage, SUPPORTED_LOCALES } from './localeSelection'

// 獲取儲存的語言或根據瀏覽器語言與地區推測
const getSavedLanguage = (): string => {
  // 首先檢查是否有儲存的語言偏好
  const saved = localStorage.getItem('language')
  console.log('getSavedLanguage: localStorage value =', saved)
  if (saved && SUPPORTED_LOCALES.includes(saved)) {
    console.log('getSavedLanguage: Using saved language =', saved)
    return saved
  }
  
  // 檢查是否有從位置偵測的偏好語言
  const preferred = localStorage.getItem('preferred-language')
  if (preferred && SUPPORTED_LOCALES.includes(preferred)) {
    // 將其儲存為語言偏好
    localStorage.setItem('language', preferred)
    return preferred
  }

  const browserLanguages = navigator.languages?.length ? navigator.languages : [navigator.language]
  return resolveInitialLanguage(saved, preferred, browserLanguages)
}

// 初始化 i18n
const initializeI18n = async () => {
  console.log('🚀 Initializing i18n...')
  console.log('📋 Supported locales:', SUPPORTED_LOCALES)

  // 優化：只載入當前語言和英語備用，其他語言按需加載
  const defaultLanguage = getSavedLanguage()
  const initialLocales = ['en'] // 始終加載英語作為備用
  if (defaultLanguage !== 'en' && !initialLocales.includes(defaultLanguage)) {
    initialLocales.push(defaultLanguage) // 加載當前語言
  }

  console.log('⚡ Initial load strategy: Loading only', initialLocales, '- other languages will load on-demand')

  // 预加载常用語言包（如果構建時已分組）
  // 這会在後台加載，不阻塞页面初始化
  if (typeof import.meta.env.VITE_ENABLE_PRELOAD !== 'undefined' && import.meta.env.VITE_ENABLE_PRELOAD !== 'false') {
    preloadCommonLanguagePacks().catch(err => {
      console.warn('❌ Failed to preload common language packs:', err)
    })
  }

  // 載入 YAML 翻譯（只加載必要的語言）
  const messages = await loadAllYamlTranslations(initialLocales)

  console.log('📦 Loaded messages:', Object.keys(messages))
  console.log('🌐 Default locale:', getSavedLanguage())

  return createI18n({
    legacy: false, // 使用 Composition API 模式
    locale: getSavedLanguage(),
    fallbackLocale: 'en',
    globalInjection: true,
    silentTranslationWarn: false,
    silentFallbackWarn: false,
    messages,
    datetimeFormats: {
      en: {
        short: {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        },
        long: {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          weekday: 'long',
          hour: 'numeric',
          minute: 'numeric'
        }
      },
      'zh-TW': {
        short: {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        },
        long: {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          weekday: 'long',
          hour: 'numeric',
          minute: 'numeric'
        }
      },
      hi: {
        short: {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        },
        long: {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          weekday: 'long',
          hour: 'numeric',
          minute: 'numeric'
        }
      },
      sw: {
        short: {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        },
        long: {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          weekday: 'long',
          hour: 'numeric',
          minute: 'numeric'
        }
      },
      th: {
        short: {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        },
        long: {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          weekday: 'long',
          hour: 'numeric',
          minute: 'numeric'
        }
      },
      fr: {
        short: {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        },
        long: {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          weekday: 'long',
          hour: 'numeric',
          minute: 'numeric'
        }
      }
    },
    numberFormats: {
      en: {
        currency: {
          style: 'currency',
          currency: 'USD'
        },
        decimal: {
          style: 'decimal',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }
      },
      'zh-TW': {
        currency: {
          style: 'currency',
          currency: 'TWD'
        },
        decimal: {
          style: 'decimal',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }
      },
      hi: {
        currency: {
          style: 'currency',
          currency: 'INR'
        },
        decimal: {
          style: 'decimal',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }
      },
      sw: {
        currency: {
          style: 'currency',
          currency: 'USD'
        },
        decimal: {
          style: 'decimal',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }
      },
      th: {
        currency: {
          style: 'currency',
          currency: 'THB'
        },
        decimal: {
          style: 'decimal',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }
      },
      fr: {
        currency: {
          style: 'currency',
          currency: 'EUR'
        },
        decimal: {
          style: 'decimal',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }
      }
    }
  })
}

// 建立並匯出 i18n 實例
let i18nInstance: any = null

// 初始化函數
export const createI18nInstance = async () => {
  if (!i18nInstance) {
    i18nInstance = await initializeI18n()
  }
  return i18nInstance
}

// 設置 i18n 實例（從外部設置）
export const setI18nInstance = (instance: any) => {
  i18nInstance = instance
}

// 動態加載缺失語言（懶加載）
export const ensureLanguageLoaded = async (locale: string) => {
  const mappedLocale = locale === 'zh-TW' || locale === 'zh-CN' || locale === 'zh-HK' ? 'zh' : locale

  if (!SUPPORTED_LOCALES.includes(mappedLocale) || !i18nInstance) {
    return false
  }

  // 檢查語言是否已加載
  if (i18nInstance.global.messages[mappedLocale]) {
    console.log(`✅ Language ${mappedLocale} already loaded`)
    return true
  }

  // 動態加載缺失的語言
  console.log(`📦 Lazy loading language: ${mappedLocale}`)
  try {
    const { loadModularYamlTranslations } = await import('./yamlLoader')
    const fileLocale = mappedLocale === 'zh' ? 'zh-TW' : mappedLocale
    const translations = await loadModularYamlTranslations(fileLocale, {
      useModules: true,
      fallbackToMonolith: true
    })

    // 設置加載的翻譯
    i18nInstance.global.setLocaleMessage(mappedLocale, translations)
    console.log(`✅ Successfully loaded ${mappedLocale}`)
    return true
  } catch (error) {
    console.error(`❌ Failed to lazy load language ${mappedLocale}:`, error)
    return false
  }
}

// 更新語言並儲存偏好的函數
export const setLanguage = async (locale: string) => {
  // Map locale codes to supported locales (e.g., zh-TW -> zh)
  const mappedLocale = locale === 'zh-TW' || locale === 'zh-CN' || locale === 'zh-HK' ? 'zh' : locale

  if (!SUPPORTED_LOCALES.includes(mappedLocale) || !i18nInstance) {
    return false
  }

  // 確保語言已加載（如果需要則動態加載）
  await ensureLanguageLoaded(mappedLocale)

  i18nInstance.global.locale.value = mappedLocale
  localStorage.setItem('language', mappedLocale)
  return true
}

// 獲取當前語言的函數
export const getCurrentLanguage = () => {
  if (i18nInstance) {
    return i18nInstance.global.locale.value
  }
  return getSavedLanguage()
}

// 獲取可用語言的函數
export const getAvailableLanguages = () => {
  return SUPPORTED_LOCALES
}

// 同步匯出（用於向後相容）
export default {
  install: async (app: any) => {
    const instance = await createI18nInstance()
    app.use(instance)
  }
}

// 匯出 TypeScript 類型
export type MessageSchema = Record<string, unknown>
